import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  catchError,
  firstValueFrom,
  from,
  lastValueFrom,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  toArray,
} from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { AuthService } from './auth.service';
import * as uuid from 'uuid';
import { convertLength } from '@turf/turf';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly _http = inject(HttpClient);
  private readonly _auth = inject(AuthService);
  private sanitizer = inject(DomSanitizer);

  constructor() {}

  executeUpload(filesToUpload: any[], idsToRemove: any[]) {
    return merge(
      from(idsToRemove ?? []).pipe(
        mergeMap((id) =>
          from(this.deleteFile(id)).pipe(
            map(() => ({ status: 'success' })),
            catchError((error) => of({ status: 'error', reason: error })),
          ),
        ),
      ),
      from(filesToUpload ?? []).pipe(
        mergeMap(({ file, metadata }) =>
          from(
            this.uploadFile(Array.isArray(file) ? file : [file], metadata),
          ).pipe(
            mergeMap((res) => res),
            map((res) => ({ status: 'success', res })),
            catchError((error) => of({ status: 'error', reason: error })),
          ),
        ),
      ),
    ).pipe(toArray());
  }

  deleteFile(id: any) {
    const token = this.getValidAccessToken();

    return this._http.delete(environment.hasura.storage + '/files/' + id, {
      ...(token
        ? {
            headers: new HttpHeaders({
              Authorization: `Bearer ${token}`,
            }),
          }
        : {}),
    });
  }

  getVersion() {
    return this._http.get(environment.hasura.storage + '/version');
  }

  getFile(id: string, name: string, old?: any) {
    return this._http
      .get(environment.hasura.storage + '/files/' + id, {
        responseType: 'blob',
      })
      .pipe(
        map((blob: any) => {
          let objectURL = URL.createObjectURL(blob);
          return {
            id,
            old,
            file: new File(
              [blob as any], // cast as any
              name,
              {
                lastModified: new Date().getTime(),
                type: blob.type,
              },
            ),
            ...(blob.type.startsWith('image')
              ? { objectURL: this.sanitizer.bypassSecurityTrustUrl(objectURL) }
              : {}),
          };
        }),
      );
  }

  scarica(file: any) {
    const newWindow = window.open('', '_blank'); // apre subito la finestra

    if (!newWindow) {
      alert(
        'Impossibile aprire la finestra. Controlla che il blocco pop-up sia disattivato.',
      );
      return;
    }

    this.getFile(file.id, file.name!)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!res?.file) return;
        const url = URL.createObjectURL(res.file as any);
        newWindow.location.href = url;
      });
  }

  isImage(file: { name?: string | null } | null | undefined): boolean {
    return /\.(jpe?g|png|webp|gif|bmp)$/i.test(file?.name?.trim() ?? '');
  }

  /** Scarica come PDF un'immagine salvata nello storage. */
  scaricaComePdf(file: { id: string; name: string }): void {
    this.getFile(file.id, file.name)
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        const blob = res?.file as Blob | undefined;
        if (!blob) return;
        void this.blobImmagineToPdf(blob, file.name);
      });
  }

  /** Converte un'immagine (Blob/File locale) in PDF A4 e la scarica. */
  async blobImmagineToPdf(blob: Blob, name: string): Promise<void> {
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Immagine non leggibile'));
        el.src = objectUrl;
      });

      // Rasterizzo su canvas: gestisce uniformemente webp/gif/bmp e rimuove l'alpha su sfondo bianco
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

      const { jsPDF } = await import('jspdf');
      const landscape = canvas.width > canvas.height;
      const doc = new jsPDF({
        orientation: landscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      const margin = 10;
      const maxW = doc.internal.pageSize.getWidth() - margin * 2;
      const maxH = doc.internal.pageSize.getHeight() - margin * 2;
      const ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      doc.addImage(
        dataUrl,
        'JPEG',
        (doc.internal.pageSize.getWidth() - w) / 2,
        (doc.internal.pageSize.getHeight() - h) / 2,
        w,
        h,
      );
      doc.save(`${name.replace(/\.[^.]+$/, '') || 'documento'}.pdf`);
    } catch (e) {
      console.error('[StorageService] conversione PDF fallita', e);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  getFileUrl(id: string) {
    return this._http
      .get(environment.hasura.storage + '/files/' + id, {
        responseType: 'blob',
      })
      .pipe(map((blob) => URL.createObjectURL(blob)));
  }

  async uploadFile(files: File[], metadata: { [key: string]: string }) {
    const token = this.getValidAccessToken();

    const formData = new FormData();
    // senza bucket-id lo storage usa "default", che non esiste su tutti gli ambienti (su DEV c'è solo "j4g")
    formData.append('bucket-id', environment.hasura.storageBucket);
    files.forEach((file) => {
      formData.append('file[]', file);
      formData.append(
        'metadata[]',
        JSON.stringify({
          metadata: {
            ...metadata,
            unique: (file as any)?.unique,
          },
        }),
      );
    });

    return this._http.post(environment.hasura.storage + '/files', formData, {
      ...(token
        ? {
            headers: new HttpHeaders({
              Authorization: `Bearer ${token}`,
            }),
          }
        : {}),
    });
  }

  private getValidAccessToken(): string | null {
    const token = this._auth.getAccessToken();
    if (!token) {
      return null;
    }

    return this._auth.isAuthenticated() ? token : null;
  }
}
